import React, { useState, useContext, useEffect } from "react";
import { DesignProgramContext } from "../pages/CustomHooks/ContextApi";

const DevicePriority = () => {
  const { derPriority, setDerPriority } = useContext(DesignProgramContext);
  
  // Helper function to normalize data - ensure all categories have all three priority fields
  const normalizeData = (data) => {
    const normalized = {};
    Object.keys(data).forEach((key) => {
      normalized[key] = {
        pmPriority: data[key]?.pmPriority ?? 0,
        derPriority: data[key]?.derPriority ?? 0,
        cbPriority: data[key]?.cbPriority ?? 0,
      };
    });
    return normalized;
  };

  const [selectedData, setSelectedData] = useState(normalizeData(derPriority));
  const [initialData, setInitialData] = useState(normalizeData(derPriority));
  const priorityTypes = ["pmPriority", "cbPriority", "derPriority"];

  useEffect(() => {
    setDerPriority(selectedData);
  }, [selectedData]);
  const handlePriorityChange = (category, changedType, newValueStr) => {
    const newValue = parseInt(newValueStr);

    setSelectedData((prevData) => {
      const newCategoryState = { ...prevData[category] };
      newCategoryState[changedType] = newValue;

      const priorityTypes = ["pmPriority", "cbPriority", "derPriority"];

      const adjustOtherPriorities = () => {
        // If setting to initial "NA" (0), skip priority adjustment
        if (newValue === 0) {
          return;
        }

        // Special handling for Utility category - only has PM & CB Priority
        if (category === "Utility") {
          const otherType = changedType === "pmPriority" ? "cbPriority" : "pmPriority";
          
          if (newValue === -1) {
            // If one is "Do Not Use", set the other to 1
            newCategoryState[otherType] = 1;
          } else if (newValue === 2) {
            // If one is 2, set the other to 1
            newCategoryState[otherType] = 1;
          } else if (newValue === 1) {
            // If one is 1, the other can be -1 or 2, but not 1
            const currentOtherValue = newCategoryState[otherType];
            if (currentOtherValue === 1) {
              // If other is also 1, change it to 2
              newCategoryState[otherType] = 2;
            }
          }
          return;
        }

        const otherTypes = priorityTypes.filter(
          (type) => type !== changedType && newCategoryState[type] !== -1 && newCategoryState[type] !== 0
        );

        if (newValue === -1) {
          // If setting to "Do Not Use", distribute remaining priorities among other types
          if (otherTypes.length === 2) {
            newCategoryState[otherTypes[0]] = 1;
            newCategoryState[otherTypes[1]] = 2;
          } else if (otherTypes.length === 1) {
            newCategoryState[otherTypes[0]] = 1;
          }
        } else if (newValue === 1) {
          otherTypes.forEach((type, index) => {
            newCategoryState[type] = index + 2;
          });
        } else if (newValue === 2) {
          if (otherTypes.length === 1) {
            newCategoryState[otherTypes[0]] = 1;
          } else if (otherTypes.length === 2) {
            newCategoryState[otherTypes[0]] = 1;
            newCategoryState[otherTypes[1]] = 3;
          }
        } else if (newValue === 3) {
          otherTypes.forEach((type, index) => {
            newCategoryState[type] = index + 1;
          });
        }
      };

      adjustOtherPriorities();
      if (category === "Utility" || category === "Load") {
        newCategoryState.derPriority = 0;
      }

      return {
        ...prevData,
        [category]: newCategoryState,
      };
    });
  };

  const derAssetsList = {
    Utility: 0,
    Genset: 1,
    BESS: 2,
    Inverter: 3,
    Load: 4,
    "Circuit Breaker": 5,
    "Power Meter": 6,
  };

  const getPriority = (key) => {
    if (key.startsWith("Load")) {
      return derAssetsList.Load;
    } else if (key === "BESS") {
      return derAssetsList.BESS;
    } else if (key === "Utility") {
      return derAssetsList.Utility;
    } else {
      return derAssetsList.Genset;
    }
  };

  const getAvailablePriorities = (category, type) => {
    const isUtilityOrLoad =
      category === "Utility" || category.toLowerCase().includes("load");
    const pmPriority = selectedData[category]?.pmPriority;
    const derPriority = selectedData[category]?.derPriority;
    const cbPriority = selectedData[category]?.cbPriority;

    // If derPriority is not allowed for this category
    if (isUtilityOrLoad && type === "derPriority") {
      return [];
    }

    // Special handling for Utility category - only options are [-1, 1, 2]
    if (category === "Utility") {
      // If other field is 2, this field can only be [-1, 1]
      if (type === "pmPriority" && cbPriority === 2) {
        return [-1, 1];
      }
      if (type === "cbPriority" && pmPriority === 2) {
        return [-1, 1];
      }
      
      // Can't select 1 for both PM & CB
      if (type === "pmPriority" && cbPriority === 1) {
        return [-1, 2]; // Can't use 1 if CB is already 1
      }
      if (type === "cbPriority" && pmPriority === 1) {
        return [-1, 2]; // Can't use 1 if PM is already 1
      }
      return [-1, 1, 2];
    }

    // Remove "Do Not Use" for Load category
    const isLoad = category.toLowerCase().includes("load");

    // Count how many times "Do Not Use" (-1) is already selected
    const doNotUseCount = [pmPriority, derPriority, cbPriority].filter(
      (p) => p === -1
    ).length;

    // Count how many dropdowns are actually available (not initial NA value of 0)
    const availableCount = [pmPriority, derPriority, cbPriority].filter(
      (p) => p !== 0
    ).length;

    if (isLoad) {
      // For Load, do not include "Do Not Use"
      if (availableCount === 1) {
        return [1];
      } else if (availableCount === 2) {
        return [1, 2];
      } else {
        return [1, 2, 3];
      }
    }

    // If 2 "Do Not Use" are already selected, show "Do Not Use" (disabled) and only option 1
    if (doNotUseCount >= 2) {
      return [-1, 1];
    }

    // If only 1 dropdown available (2 others are NA), show only [-1, 1]
    if (availableCount === 1) {
      return [-1, 1];
    }

    // If 1 "Do Not Use" is already selected
    if (doNotUseCount >= 1) {
      // If only 2 dropdowns available, only offer [-1, 1]
      if (availableCount === 2) {
        return [-1, 1];
      }
      // If 3 dropdowns available, offer [-1, 1, 2]
      return [-1, 1, 2];
    }

    // Build available priorities - "Do Not Use" is always included
    let availablePriorities = [-1];

    // Add numeric priorities based on available dropdowns
    if (availableCount === 2) {
      // Only 2 dropdowns available, so only 1, 2 are needed
      availablePriorities.push(1, 2);
    } else {
      // All 3 dropdowns available, so 1, 2, 3 are needed
      availablePriorities.push(1, 2, 3);
    }

    return availablePriorities;
  };

  const isDoNotUseDisabled = (category) => {
    const pmPriority = selectedData[category]?.pmPriority;
    const derPriority = selectedData[category]?.derPriority;
    const cbPriority = selectedData[category]?.cbPriority;
    
    // Only count available dropdowns (not 0)
    const availablePriorities = [
      pmPriority !== 0 ? pmPriority : undefined,
      derPriority !== 0 ? derPriority : undefined,
      cbPriority !== 0 ? cbPriority : undefined,
    ].filter((p) => p !== undefined);
    
    // Count how many times "Do Not Use" (-1) is already selected among available dropdowns
    const doNotUseCount = availablePriorities.filter((p) => p === -1).length;
    
    // Count available dropdowns
    const availableCount = availablePriorities.length;
    
    // For Load category, "Do Not Use" is not available at all
    const isLoad = category.toLowerCase().includes("load");
    if (isLoad) {
      return true;
    }
    
    // Disable "Do Not Use" if already (availableCount - 1) are selected
    // This ensures at least one priority option is always available
    return doNotUseCount >= availableCount - 1;
  };

  const resetDevicePriority = () => {
    setSelectedData(initialData);
  };

  const saveDevicePriority = () => {
    console.log("selectedData", selectedData);
  };

  return (
    <div>
      <div className="d-flex justify-content-end">
        <se-button color="primary" onClick={resetDevicePriority}>
          Reset
        </se-button>
      </div>

      <div className="table-responsive mt-2">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>DERS Name</th>
              <th>PM Priority</th>
              <th>CB Priority</th>
              <th>DER Priority</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(selectedData).filter((category) => category !== "Grounding_Transformer")
              .sort((a, b) => {
                return getPriority(a) - getPriority(b);
              })
              .map((category) => (
                <tr key={category}>
                  <td>{category}</td>
                  <td>
                    {selectedData[category]?.pmPriority === 0 ? (
                      <div style={{ textAlign: "center", paddingTop: "9px" }}>
                        NA
                      </div>
                    ) : (
                      <select
                        className="form-control form-control-sm"
                        value={String(selectedData[category]?.pmPriority ?? 1)}
                        onChange={(e) =>
                          handlePriorityChange(
                            category,
                            "pmPriority",
                            e.target.value
                          )
                        }
                      >
                        {getAvailablePriorities(category, "pmPriority").map(
                          (priority) => (
                            <option
                              key={priority}
                              value={String(priority)}
                              disabled={priority === -1 && isDoNotUseDisabled(category)}
                            >
                              {priority === -1 ? "Do Not Use" : priority}
                            </option>
                          )
                        )}
                      </select>
                    )}
                  </td>
                  <td>
                    {selectedData[category]?.cbPriority === 0 ? (
                      <div style={{ textAlign: "center", paddingTop: "9px" }}>
                        NA
                      </div>
                    ) : (
                      <select
                        className="form-control form-control-sm"
                        value={String(selectedData[category]?.cbPriority ?? 2)}
                        onChange={(e) =>
                          handlePriorityChange(
                            category,
                            "cbPriority",
                            e.target.value
                          )
                        }
                      >
                        {getAvailablePriorities(category, "cbPriority").map(
                          (priority) => (
                            <option
                              key={priority}
                              value={String(priority)}
                              disabled={priority === -1 && isDoNotUseDisabled(category)}
                            >
                              {priority === -1 ? "Do Not Use" : priority}
                            </option>
                          )
                        )}
                      </select>
                    )}
                  </td>
                  <td>
                    {category === "Utility" ||
                    category.toLowerCase().includes("load") ? (
                      <div style={{ textAlign: "center", paddingTop: "9px" }}>
                        NA
                      </div>
                    ) : selectedData[category]?.derPriority === 0 ? (
                      <div style={{ textAlign: "center", paddingTop: "9px" }}>
                        NA
                      </div>
                    ) : (
                      <select
                        className="form-control form-control-sm"
                        value={String(selectedData[category]?.derPriority ?? -1)}
                        onChange={(e) =>
                          handlePriorityChange(
                            category,
                            "derPriority",
                            e.target.value
                          )
                        }
                      >
                        {getAvailablePriorities(category, "derPriority").map(
                          (priority) => (
                            <option
                              key={priority}
                              value={String(priority)}
                              disabled={priority === -1 && isDoNotUseDisabled(category)}
                            >
                              {priority === -1 ? "Do Not Use" : priority}
                            </option>
                          )
                        )}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DevicePriority;
